import { useConversationReport } from "@coaching-culture/api-client";
import { ConversationReportItem, UserFilter } from "@coaching-culture/types";
import {
  Button,
  DatePill,
  DateRangeButton,
  Flex,
  Panel,
  PanelHeader,
  Select,
  SortableTable,
  SortableTableColumn,
  Text,
  UserFlag,
} from "@coaching-culture/ui";
import { FilterModal } from "components/FilterPanel";
import {
  addDays,
  endOfDay,
  format,
  fromUnixTime,
  getUnixTime,
  startOfDay,
} from "date-fns";
import { uniq } from "lodash";
import { unparse } from "papaparse";
import { useMemo, useState } from "react";
import { FaCommentDots } from "react-icons/fa";
import { downloadContent } from "utils";

export const ConversationsReport = () => {
  const [typeFilter, setTypeFilter] = useState("");
  const [filter, setFilter] = useState<UserFilter>({
    groups: [],
    userFields: [],
  });
  const [range, setRange] = useState<[Date, Date]>([
    startOfDay(addDays(new Date(), -30)),
    endOfDay(new Date()),
  ]);

  const rangeStart = getUnixTime(range[0]);
  const rangeEnd = getUnixTime(range[1]);

  const [showFilters, setShowFilters] = useState(false);

  const onCancel = (result: UserFilter | null) => {
    if (result) {
      setFilter(result);
    }

    setShowFilters(false);
  };

  const report = useConversationReport(filter);

  const data = (report.data ?? [])
    .filter((x) => x.scheduledDate >= rangeStart && x.scheduledDate <= rangeEnd)
    .filter((x) => typeFilter === "" || x.conversationType === typeFilter);

  const cols = useMemo<SortableTableColumn<ConversationReportItem>[]>(
    () => [
      {
        label: "Manager",
        name: "manager",
        format: (x) => <UserFlag user={x.manager} />,
        exportFormat: (x) => x.manager.email,
        sortFormat: (x) => x.manager.name,
      },
      {
        label: "Direct Report",
        name: "directReport",
        format: (x) => <UserFlag user={x.directReport} />,
        exportFormat: (x) => x.directReport.email,
        sortFormat: (x) => x.directReport.name,
      },
      {
        label: "Type",
        name: "conversationType",
        format: (x) => x.conversationType,
      },
      {
        label: "Completed Date",
        name: "completedDate",
        exportFormat: (x) =>
          format(fromUnixTime(x.completedDate), "dd/MM/yyyy HH:mm"),
        headingProps: {
          style: { textAlign: "right" },
        },
        props: {
          style: { textAlign: "right" },
        },
        format: (x) => <DatePill value={x.completedDate} dateOnly />,
      },
    ],
    [],
  );

  function handleExport(data: Record<string, string>[]): void {
    downloadContent(
      unparse(data),
      `ConversationReport-${new Date().toLocaleDateString()}.csv`,
    );
  }

  const typeOptions = [
    { value: "", label: "(All Types)" },
    ...uniq((report.data ?? []).map((x) => x.conversationType)).map((x) => ({
      value: x,
      label: x,
    })),
  ];

  return (
    <div>
      <FilterModal
        isOpen={showFilters}
        filter={filter}
        onClose={(result) => {
          onCancel(result);
        }}
      />
      <Flex mb={3} justifyContent={"flex-end"}>
        <Button color="primary" onClick={() => setShowFilters(true)}>
          Filters ({filter.groups.length + filter.userFields.length})
        </Button>

        <DateRangeButton
          ml={2}
          value={range}
          onChange={(val) => setRange([startOfDay(val[0]), endOfDay(val[1])])}
          noNull
          presets={[14, 30, 90, 180, 365]}
        />
      </Flex>
      <Panel>
        <PanelHeader
          justifyContent={"space-between"}
          flexDirection={["column", "row"]}
          alignItems={["flex-start", "center"]}
          height="auto"
          style={{ gap: 12 }}
        >
          <Text
            fontSize="title"
            fontWeight={600}
            color="black"
            style={{ flex: 1 }}
          >
            Completed conversations history
          </Text>
          <Select
            maxWidth={300}
            value={typeFilter}
            options={typeOptions}
            onChange={(ev) => setTypeFilter(ev.target.value)}
          />
        </PanelHeader>
        <SortableTable
          paged
          columns={cols}
          data={data}
          onExport={handleExport}
          emptyMessage={"No completed conversations in this date range."}
          emptyIcon={FaCommentDots}
        />
      </Panel>
    </div>
  );
};
